import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Package, ChefHat, CheckCircle, Truck, Home, Star, MessageSquare, X, ThumbsUp, ThumbsDown, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';

const OrderTrackingPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eta, setEta] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  
  // Rating states
  const [orderRating, setOrderRating] = useState(0);
  const [orderReview, setOrderReview] = useState('');
  const [productRatings, setProductRatings] = useState({});
  const [packagingFeedback, setPackagingFeedback] = useState(null);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);

  const statusSteps = [
    { key: 'placed', label: 'Order Placed', icon: Package, message: 'Order received 👍' },
    { key: 'accepted', label: 'Accepted', icon: CheckCircle, message: 'Kitchen confirmed 👨‍🍳' },
    { key: 'preparing', label: 'Preparing', icon: ChefHat, message: 'Cooking your order 🔥' },
    { key: 'ready', label: 'Ready', icon: Package, message: 'Packed & ready 🎁' },
    { key: 'out_for_delivery', label: 'On the Way', icon: Truck, message: 'On the way 🚚' },
    { key: 'delivered', label: 'Delivered', icon: Home, message: 'Delivered ✅' },
  ];

  useEffect(() => {
    fetchOrder();
    fetchEta();
    const interval = setInterval(() => {
      fetchOrder();
      fetchEta();
    }, 15000);
    return () => clearInterval(interval);
  }, [orderId]);

  useEffect(() => {
    if (remainingTime <= 0 || order?.status === 'delivered') return;
    const timer = setInterval(() => {
      setRemainingTime(prev => Math.max(0, prev - 1));
    }, 60000);
    return () => clearInterval(timer);
  }, [remainingTime, order?.status]);

  // Set initial rating states when order loads
  useEffect(() => {
    if (order) {
      setOrderRating(order.order_rating || 0);
      setOrderReview(order.order_review || '');
    }
  }, [order]);

  const fetchOrder = async () => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      setOrder(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching order:', error);
      setLoading(false);
    }
  };

  const fetchEta = async () => {
    try {
      const response = await api.get(`/orders/${orderId}/eta`);
      setEta(response.data);
      setRemainingTime(response.data.remaining_minutes);
    } catch (error) {
      console.error('Error fetching ETA:', error);
    }
  };

  // Handle overall order rating
  const handleOrderRating = async (rating) => {
    setOrderRating(rating);
    try {
      await api.post('/orders/rate', {
        order_id: order.id,
        rating: rating,
        review: orderReview
      }, {
        headers: { 'x-customer-phone': order.customer_phone }
      });
      toast.success('Thanks for rating your order!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rating');
    }
  };

  // Handle order review submission
  const handleSubmitReview = async () => {
    if (!orderReview.trim()) return;
    
    setIsSubmittingRating(true);
    try {
      await api.post('/orders/rate', {
        order_id: order.id,
        rating: orderRating || 5,
        review: orderReview
      }, {
        headers: { 'x-customer-phone': order.customer_phone }
      });
      toast.success('Review submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setIsSubmittingRating(false);
    }
  };

  // Handle product rating (inline)
  const handleProductRating = async (productId, rating) => {
    setProductRatings(prev => ({ ...prev, [productId]: rating }));
    
    try {
      await api.post(`/products/${productId}/rate`, {
        product_id: productId,
        order_id: order.id,
        rating: rating
      }, {
        headers: { 'x-customer-phone': order.customer_phone }
      });
      toast.success('Product rated!');
    } catch (error) {
      if (!error.response?.data?.detail?.includes('already rated')) {
        toast.error(error.response?.data?.detail || 'Failed to rate product');
      }
    }
  };

  const handleRaiseTicket = async () => {
    if (!ticketSubject || !ticketDescription) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await api.post('/tickets', {
        order_id: order.id,
        subject: ticketSubject,
        description: ticketDescription
      }, {
        headers: { 'x-customer-phone': order.customer_phone }
      });
      toast.success('Support ticket created successfully');
      setShowTicketModal(false);
      setTicketSubject('');
      setTicketDescription('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    }
  };

  // Handle invoice download
  const handleDownloadInvoice = async () => {
    try {
      const response = await api.get(`/orders/${order.id}/invoice`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${order.order_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded!');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  // Star rating component
  const StarRating = ({ rating, onRate, size = 'md', disabled = false }) => {
    const sizeClass = size === 'sm' ? 'w-5 h-5' : size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => !disabled && onRate(star)}
            disabled={disabled}
            className={`focus:outline-none transition-transform ${!disabled && 'hover:scale-110'}`}
          >
            <Star
              className={`${sizeClass} ${
                star <= rating 
                  ? 'fill-yellow-400 text-yellow-400' 
                  : 'text-gray-500'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-white text-xl">Loading order...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl mb-4">Order not found</p>
          <Link to="/">
            <Button className="bg-[#FF4500]">Back to Menu</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isDelivered = order.status === 'delivered' || order.status === 'completed';
  const displaySteps = order.order_type === 'pickup' 
    ? statusSteps.filter(s => !['out_for_delivery'].includes(s.key))
    : statusSteps;
  const currentStepIndex = displaySteps.findIndex(s => s.key === order.status);
  const progressPercent = eta?.progress_percent || ((currentStepIndex + 1) / displaySteps.length) * 100;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#121212] border-b border-white/10 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/orders" className="text-white hover:text-[#FF4500] transition-colors">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div className="text-center">
              <h1 className="text-xl font-bold text-white">Order Status</h1>
              <p className="text-[#FF4500] text-sm">{order.order_number}</p>
            </div>
            <div className="w-6"></div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-lg">
        {/* ETA Card - Only for non-delivered orders */}
        {!isDelivered && (() => {
          // Calculate estimated time - use ETA API data, or fallback to order estimates
          const prepTime = order.prep_time_estimate || 20;
          const deliveryTime = order.order_type === 'delivery' ? (order.delivery_time_estimate || 15) : 0;
          const bufferTime = order.buffer_time || 5;
          const totalEstimate = prepTime + deliveryTime + bufferTime;
          
          // Check if preparation has started (countdown should only start after preparing)
          const preparationStarted = ['preparing', 'ready', 'out_for_delivery'].includes(order.status);
          
          // Calculate time based on preparation status
          const orderCreated = new Date(order.created_at);
          const statusUpdated = order.status_updated_at ? new Date(order.status_updated_at) : orderCreated;
          const now = new Date();
          
          let displayTime;
          let isDelayed = false;
          let delayMins = 0;
          
          if (!preparationStarted) {
            // Before preparation - show fixed estimated time (no countdown)
            displayTime = totalEstimate;
          } else {
            // After preparation started - calculate remaining time from when prep started
            const prepStartTime = statusUpdated; // Use status_updated_at as prep start time
            const expectedDelivery = new Date(prepStartTime.getTime() + (prepTime + deliveryTime + bufferTime) * 60000);
            
            displayTime = Math.max(0, Math.round((expectedDelivery - now) / 60000));
            
            // Check if delayed
            isDelayed = now > expectedDelivery;
            delayMins = isDelayed ? Math.round((now - expectedDelivery) / 60000) : 0;
            
            // If delayed, add extra buffer time
            if (isDelayed) {
              if (delayMins <= 10) {
                displayTime = 5 + Math.ceil(delayMins / 2);
              } else if (delayMins <= 20) {
                displayTime = 10 + Math.ceil(delayMins / 3);
              } else {
                displayTime = 15 + Math.ceil(delayMins / 4);
              }
            }
          }
          
          // Only show delay message if delayed by more than 15 mins
          const showDelayMessage = delayMins > 15;
          
          // Round to nearest 5 mins for cleaner display
          displayTime = Math.ceil(displayTime / 5) * 5;
          if (displayTime < 5) displayTime = 5;
          
          // Status-specific messages
          const getStatusMessage = () => {
            if (showDelayMessage) return 'On the way! Almost there 🚚';
            switch (order.status) {
              case 'placed': return 'Order received 👍';
              case 'accepted': return 'Kitchen confirmed 👨‍🍳';
              case 'preparing': return 'Cooking your order 🔥';
              case 'ready': return order.order_type === 'pickup' ? 'Ready for pickup! 🎁' : 'Packed & ready 📦';
              case 'out_for_delivery': return 'On the way! 🚚';
              default: return eta?.status_message || 'Processing...';
            }
          };
          
          return (
            <>
              {/* Delay Alert Banner - Only show if delayed by more than 15 mins */}
              {showDelayMessage && (
                <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-xl p-4 mb-4 text-center">
                  <p className="text-yellow-400 font-medium text-sm">
                    ⏰ Your order is taking a bit longer than usual. Thanks for your patience! 🙏
                  </p>
                </div>
              )}
              
              <div className={`rounded-2xl p-6 mb-6 text-center shadow-lg ${
                showDelayMessage 
                  ? 'bg-gradient-to-r from-yellow-500 to-orange-500' 
                  : 'bg-gradient-to-r from-orange-500 to-orange-600'
              }`}>
                <p className="text-white/80 text-sm mb-1">
                  {getStatusMessage()}
                </p>
                <p className="text-white text-4xl font-bold mb-2">
                  {displayTime} mins
                </p>
                <p className="text-white/90 text-lg font-medium">
                  {!preparationStarted 
                    ? 'Estimated time' 
                    : (order.order_type === 'delivery' ? 'Delivery time' : 'Ready for pickup')}
                </p>
              </div>
            </>
          );
        })()}

        {/* Delivered Card */}
        {isDelivered && (
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 mb-6 text-center shadow-lg">
            <CheckCircle className="w-12 h-12 text-white mx-auto mb-2" />
            <p className="text-white text-2xl font-bold">
              {order.order_type === 'delivery' ? 'Delivered!' : 'Order Complete!'}
            </p>
          </div>
        )}

        {/* Progress Steps - Only for non-delivered orders */}
        {!isDelivered && (
          <div className="bg-[#121212] rounded-2xl p-6 mb-6">
            <div className="space-y-4">
              {displaySteps.map((step, index) => {
                const StepIcon = step.icon;
                const isCompleted = index <= currentStepIndex;
                const isCurrent = index === currentStepIndex;

                return (
                  <div key={step.key} className="flex items-center gap-4">
                    <div className={`
                      w-12 h-12 rounded-full flex items-center justify-center transition-all
                      ${isCompleted ? 'bg-[#FF4500] text-white' : 'bg-white/10 text-gray-500'}
                      ${isCurrent ? 'ring-4 ring-[#FF4500]/30 scale-110' : ''}
                    `}>
                      <StepIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className={`font-semibold ${isCompleted ? 'text-white' : 'text-gray-500'}`}>
                        {step.label}
                      </p>
                      {isCurrent && eta && (
                        <p className="text-sm text-[#FF4500]">
                          {remainingTime > 0 ? `~${remainingTime} mins remaining` : 'Almost done!'}
                        </p>
                      )}
                    </div>
                    {isCompleted && !isCurrent && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ============ RATING SECTION (Only for delivered orders) ============ */}
        {isDelivered && (
          <div className="bg-[#121212] rounded-2xl p-6 mb-6 space-y-4">
            {/* Overall Order Rating */}
            <div className="text-center pb-4 border-b border-white/10">
              <p className="text-white font-medium mb-3">Rate this Order</p>
              <StarRating 
                rating={orderRating} 
                onRate={handleOrderRating} 
                size="lg"
              />
            </div>

            {/* Review Textarea */}
            <div>
              <Textarea
                placeholder="Write a review (optional)..."
                value={orderReview}
                onChange={(e) => setOrderReview(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 min-h-[80px]"
              />
              {orderReview.trim() && (
                <Button 
                  onClick={handleSubmitReview}
                  disabled={isSubmittingRating}
                  className="w-full mt-2 bg-[#FF4500] hover:bg-[#FF5722] text-white"
                  size="sm"
                >
                  {isSubmittingRating ? 'Submitting...' : 'Submit Review'}
                </Button>
              )}
            </div>

            {/* Packaging Feedback */}
            <div className="pt-4 border-t border-white/10">
              <p className="text-gray-400 text-sm mb-2">How was the packaging?</p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPackagingFeedback('good')}
                  className={`flex-1 ${packagingFeedback === 'good' ? 'bg-green-500/20 border-green-500 text-green-400' : 'border-white/20 text-white'}`}
                >
                  <ThumbsUp className="w-4 h-4 mr-1" />
                  Good
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPackagingFeedback('not_good')}
                  className={`flex-1 ${packagingFeedback === 'not_good' ? 'bg-red-500/20 border-red-500 text-red-400' : 'border-white/20 text-white'}`}
                >
                  <ThumbsDown className="w-4 h-4 mr-1" />
                  Not Good
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Order Items with inline ratings */}
        <div className="bg-[#121212] rounded-2xl p-6 mb-6">
          <h3 className="text-white font-bold mb-4">Order Items</h3>
          {order.items?.map((item, index) => {
            const itemRating = productRatings[item.product_id] || 0;
            
            return (
              <div key={index} className="py-3 border-b border-white/10 last:border-0">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <p className="text-white">{item.product_name}</p>
                    <p className="text-sm text-gray-400">Qty: {item.quantity}</p>
                  </div>
                  <span className="text-white font-medium">{formatCurrency(item.price * item.quantity)}</span>
                </div>
                
                {/* Inline Product Rating - Only for delivered orders */}
                {isDelivered && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-gray-500 text-xs">Rate:</span>
                    <StarRating 
                      rating={itemRating} 
                      onRate={(r) => handleProductRating(item.product_id, r)} 
                      size="sm"
                    />
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Order Total */}
          <div className="border-t border-white/10 mt-4 pt-4">
            <div className="flex justify-between text-white font-bold text-lg">
              <span>Total</span>
              <span className="text-[#FF4500]">{formatCurrency(order.total_amount)}</span>
            </div>
          </div>
        </div>

        {/* Delivery Info */}
        {order.order_type === 'delivery' && order.customer_address && (
          <div className="bg-[#121212] rounded-2xl p-6 mb-6">
            <h3 className="text-white font-bold mb-3">Delivery Address</h3>
            <p className="text-gray-400">{order.customer_address}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 flex-wrap">
          {/* Download Invoice Button - Only for completed orders */}
          {isDelivered && (
            <Button
              variant="outline"
              className="flex-1 min-w-[140px] border-green-500/50 text-green-400 hover:bg-green-500/10"
              onClick={handleDownloadInvoice}
            >
              <Download className="w-4 h-4 mr-2" />
              Download Invoice
            </Button>
          )}
          <Button
            variant="outline"
            className="flex-1 min-w-[100px] border-white/20 text-white hover:bg-white/10"
            onClick={() => setShowTicketModal(true)}
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Need Help?
          </Button>
          <Link to="/" className="flex-1 min-w-[100px]">
            <Button className="w-full bg-[#FF4500] hover:bg-[#FF5722]">
              Back to Menu
            </Button>
          </Link>
        </div>
      </div>

      {/* Ticket Modal */}
      <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
        <DialogContent className="bg-[#121212] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Need Help?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-400 mb-1 block">Issue Type</label>
              <select
                value={ticketSubject}
                onChange={(e) => setTicketSubject(e.target.value)}
                className="w-full p-3 bg-[#1a1a1a] border border-white/10 rounded-lg text-white [&>option]:bg-[#1a1a1a] [&>option]:text-white"
              >
                <option value="" className="bg-[#1a1a1a] text-white">Select an issue</option>
                <option value="Wrong order" className="bg-[#1a1a1a] text-white">Wrong order received</option>
                <option value="Missing items" className="bg-[#1a1a1a] text-white">Missing items</option>
                <option value="Quality issue" className="bg-[#1a1a1a] text-white">Quality issue</option>
                <option value="Delivery issue" className="bg-[#1a1a1a] text-white">Delivery issue</option>
                <option value="Payment issue" className="bg-[#1a1a1a] text-white">Payment issue</option>
                <option value="Other" className="bg-[#1a1a1a] text-white">Other</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-400 mb-1 block">Description</label>
              <Textarea
                placeholder="Describe your issue..."
                value={ticketDescription}
                onChange={(e) => setTicketDescription(e.target.value)}
                className="bg-white/5 border-white/10 text-white min-h-[120px]"
              />
            </div>
            <Button 
              onClick={handleRaiseTicket}
              disabled={!ticketSubject || !ticketDescription}
              className="w-full bg-[#FF4500] hover:bg-[#FF5722]"
            >
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderTrackingPage;
